import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Billing, Tenants, type BillingInterval, type BillingPlan } from '@/lib/api/v1';
import { Badge, Button, Card, Icon, Skeleton, Tabs } from '@/design-system';
import { formatPriceNok } from '@/lib/format';
import { resolveEntitlements, planLabel, type PlanId } from '@/lib/entitlements';
import { usePrefs, useToasts } from '@/lib/store';

/**
 * Abonnement — plan comparison + upgrade entry point. Prices/feature list
 * mirror docs/BUSINESS_MODEL.md section 2 exactly (99/899 Pluss, 590/5900
 * Pro). Upgrade buttons call POST /api/v1/billing/checkout, which 501s with
 * a clear message while BILLING_ENABLED is off (the default) — see
 * billing.enabled below, sourced from GET /api/v1/billing/status so the
 * button state reflects real backend config rather than guessing.
 */

interface TierDef {
  id: PlanId;
  name: string;
  tagline: string;
  monthlyNok: number;
  annualNok: number;
  features: string[];
  billingPlan?: BillingPlan;
}

const FREE_FEATURES = [
  'Kjellerregistrering opptil 50 flasker',
  'Strekkode- og kvitteringsskanning (delt AI-kø)',
  'Innsjekk, sosial feed og venner',
  'Husholdning: eier + inntil 2 medlemmer',
  '1 hylle',
  'Cocktailer, guider og mat-paring',
];

const PLUSS_FEATURES = [
  'Alt i Gratis, pluss:',
  'Ubegrenset antall flasker og hyller',
  'Ubegrenset AI-scanning, prioritert kø',
  'Etikettutskrift til Zebra-skriver',
  'Ubegrenset husholdningsmedlemmer',
  'Avanserte stats/badges og CSV/PDF-eksport',
];

const PRO_FEATURES = [
  'Alt i Pluss, pluss:',
  'Vinliste-generator (PDF + live lenke/QR)',
  'Varetelling',
  'Priskalkulator (innkjøp → utsalg m/ avanse)',
  'Flere utsalgspriser',
  'Flere ansattkontoer med roller',
  'API-tilgang (grunnleggende)',
];

const TIERS: TierDef[] = [
  { id: 'free', name: 'Gratis', tagline: 'For å komme i gang', monthlyNok: 0, annualNok: 0, features: FREE_FEATURES },
  {
    id: 'pluss',
    name: 'Pluss',
    tagline: 'For den private samleren',
    monthlyNok: 99,
    annualNok: 899,
    features: PLUSS_FEATURES,
    billingPlan: 'pluss',
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'For restaurant og vinbar',
    monthlyNok: 590,
    annualNok: 5900,
    features: PRO_FEATURES,
    billingPlan: 'pro',
  },
];

export function AbonnementPage() {
  const { push } = useToasts();
  const { activeTenantId } = usePrefs();
  const [interval, setInterval] = useState<BillingInterval>('monthly');

  const tenantsQ = useQuery({ queryKey: ['tenants'], queryFn: () => Tenants.list() });
  const statusQ = useQuery({ queryKey: ['billing-status'], queryFn: () => Billing.status() });

  const tenants = tenantsQ.data?.tenants ?? [];
  const activeTenant = tenants.find((t) => t.id === activeTenantId) ?? tenants[0];
  const currentPlan: PlanId = activeTenant
    ? (['free', 'pluss', 'pro', 'legacy'].includes(activeTenant.plan) ? (activeTenant.plan as PlanId) : 'free')
    : 'free';
  const capabilities = activeTenant ? resolveEntitlements(activeTenant) : undefined;
  const billingEnabled = statusQ.data?.enabled ?? false;

  const checkout = useMutation({
    mutationFn: (input: { plan: BillingPlan; interval: BillingInterval }) => Billing.checkout(input.plan, input.interval),
    onSuccess: ({ checkoutUrl }) => {
      window.location.href = checkoutUrl;
    },
    onError: (err: Error) => push({ title: 'Kunne ikke starte betaling', description: err.message, tone: 'danger' }),
  });

  const portal = useMutation({
    mutationFn: () => Billing.portal(),
    onSuccess: ({ portalUrl }) => {
      window.location.href = portalUrl;
    },
    onError: (err: Error) => push({ title: 'Kunne ikke åpne fakturaadministrasjon', description: err.message, tone: 'danger' }),
  });

  return (
    <div className="grid gap-8 max-w-5xl">
      <header className="grid gap-2">
        <h1 className="ui-section-title text-3xl">Abonnement</h1>
        <p className="max-w-2xl text-sm text-mist">
          Gratis-planen dekker en vanlig startsamling. Pluss fjerner flaskegrensen for privatsamlere. Pro legger til
          vinliste, varetelling og priskalkulator for restaurant og vinbar.
        </p>
        {activeTenant && (
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-xs uppercase tracking-widest text-mist">Nåværende plan for {activeTenant.name}:</span>
            <Badge tone={currentPlan === 'free' ? 'mist' : 'gold'}>{planLabel(currentPlan)}</Badge>
            {activeTenant.stripeCustomerId && (
              <Button size="sm" variant="ghost" iconRight="external" loading={portal.isPending} onClick={() => portal.mutate()}>
                Administrer fakturering
              </Button>
            )}
          </div>
        )}
        {!billingEnabled && (
          <div className="mt-2 flex items-start gap-2 rounded-md border border-line bg-ink-soft p-3 text-xs text-mist">
            <Icon name="info" size={14} className="mt-0.5 shrink-0" />
            <span>
              Selvbetjent kjøp kommer snart. Ta kontakt for oppgradering i mellomtiden — knappene under er deaktivert
              helt til betaling er skrudd på.
            </span>
          </div>
        )}
      </header>

      <Tabs
        tabs={[
          { id: 'monthly', label: 'Månedlig' },
          { id: 'annual', label: 'Årlig (spar 2 mnd)' },
        ]}
        active={interval}
        onChange={setInterval}
        className="justify-self-start"
      />

      {tenantsQ.isLoading || statusQ.isLoading ? (
        <div className="grid gap-4 sm:grid-cols-3">
          <Skeleton height={420} rounded="lg" />
          <Skeleton height={420} rounded="lg" />
          <Skeleton height={420} rounded="lg" />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-3">
          {TIERS.map((tier) => {
            const priceNok = interval === 'monthly' ? tier.monthlyNok : tier.annualNok;
            const isCurrent = currentPlan === tier.id || (currentPlan === 'legacy' && tier.id === 'pro');
            const isDowngrade = TIERS.findIndex((t) => t.id === tier.id) < TIERS.findIndex((t) => t.id === currentPlan) && currentPlan !== 'legacy';

            return (
              <Card
                key={tier.id}
                className={`grid gap-4 p-6 ${isCurrent ? 'border-candle shadow-glow' : ''}`}
              >
                <div className="grid gap-1">
                  <div className="flex items-center gap-2">
                    <h2 className="font-serif text-2xl text-parchment">{tier.name}</h2>
                    {isCurrent && <Badge tone="gold">Din plan</Badge>}
                  </div>
                  <p className="text-xs text-mist">{tier.tagline}</p>
                </div>

                <div className="grid gap-0.5">
                  <span className="font-serif text-3xl text-parchment">
                    {tier.monthlyNok === 0 ? 'Gratis' : formatPriceNok(priceNok)}
                  </span>
                  {tier.monthlyNok > 0 && (
                    <span className="text-xs text-mist">
                      {interval === 'monthly' ? 'per måned' : 'per år (≈ ' + Math.round(tier.annualNok / 12) + ' kr/mnd)'}
                    </span>
                  )}
                </div>

                <ul className="grid gap-1.5 text-sm text-mist">
                  {tier.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-2">
                      <Icon name="check" size={14} className="mt-0.5 shrink-0 text-candle" />
                      <span className={feature.endsWith(':') ? 'font-semibold text-parchment' : ''}>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-2">
                  {tier.id === 'free' ? (
                    <Button variant="ghost" disabled className="w-full justify-center">
                      {currentPlan === 'free' ? 'Din nåværende plan' : 'Alltid inkludert'}
                    </Button>
                  ) : isCurrent ? (
                    <Button variant="ghost" disabled className="w-full justify-center">
                      Aktiv plan
                    </Button>
                  ) : (
                    <Button
                      variant="primary"
                      className="w-full justify-center"
                      iconRight="arrow-right"
                      loading={checkout.isPending && checkout.variables?.plan === tier.billingPlan}
                      disabled={!billingEnabled || !tier.billingPlan}
                      title={!billingEnabled ? 'Kommer snart' : undefined}
                      onClick={() => tier.billingPlan && checkout.mutate({ plan: tier.billingPlan, interval })}
                    >
                      {!billingEnabled ? 'Kommer snart' : isDowngrade ? 'Bytt plan' : 'Oppgrader'}
                    </Button>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {capabilities && (
        <Card className="grid gap-3 p-5">
          <h2 className="font-serif text-lg text-parchment">Din bruk akkurat nå</h2>
          <div className="grid gap-1 text-sm text-mist sm:grid-cols-2">
            <span>Flaskegrense: {capabilities.maxBottles == null ? 'Ubegrenset' : `${capabilities.maxBottles} flasker`}</span>
            <span>
              Husholdning: {capabilities.maxHouseholdMembers == null ? 'Ubegrenset medlemmer' : `Eier + ${capabilities.maxHouseholdMembers} medlemmer`}
            </span>
            <span>Etikettutskrift: {capabilities.labelPrint ? 'Aktivert' : 'Ikke inkludert'}</span>
            <span>Vinliste-generator: {capabilities.wineListGenerator ? 'Aktivert' : 'Ikke inkludert'}</span>
            <span>Varetelling: {capabilities.stockCount ? 'Aktivert' : 'Ikke inkludert'}</span>
            <span>Priskalkulator: {capabilities.priceCalculator ? 'Aktivert' : 'Ikke inkludert'}</span>
          </div>
        </Card>
      )}
    </div>
  );
}
